import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import dataModel from '../../models/DataModel';
import Animate from '../Animate';
import FileUtil from '../../helpers/FileUtil/FileUtil';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';
import AttachmentMetadata from '../../components/AttachmentMetadata';
import Home from '../../containers/Home';

import './PhotoCarousel.scss';
import Input from '../Input';

/**
 * TODO: get rid of all references to completionStep in this component - should be generic
 */
export class PhotoCarousel extends React.Component {
  constructor(props) {
    super(props);
    PhotoCarousel.propTypes = {
      attachments: PropTypes.arrayOf(PropTypes.object).isRequired,
      completionStep: PropTypes.object,
      // ID of the photo to open, if not supplied, the first photo in the CS will be used
      initialAttachmentId: PropTypes.number,
      // Used in web view to close photo
      handleClose: PropTypes.func,
      /** whether renaming is enabled */
      hasRename: PropTypes.bool,
      /** whether deleting is enabled */
      hasDelete: PropTypes.bool,
      /** whether editing is enabled */
      hasEdit: PropTypes.bool,
      handleDelete: PropTypes.func,
    };

    this.state = {
      currentAttachment: null,
      editingEnabled: false,
      selectedIndex: null, 
      carouselLeft: 0,
      newAttachmentName: null,
      isDeleting: false,
      isEditing: false,
      isLoading: true,
    };

    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    this.changePhotoName = this.changePhotoName.bind(this);
    this.deleteAttachment = this.deleteAttachment.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleRename = this.handleRename.bind(this);
    this.maybeRevertPhotoName = this.maybeRevertPhotoName.bind(this);
    this.savePhotoRename = this.savePhotoRename.bind(this);
    this.handleArrowRight = this.handleArrowRight.bind(this);
    this.handleArrowLeft = this.handleArrowLeft.bind(this);
    this.handleLeftAnimationDone = this.handleLeftAnimationDone.bind(this);
    this.handleRightAnimationDone = this.handleRightAnimationDone.bind(this);
    this.handleInitializeElement = this.handleInitializeElement.bind(this);
    this.handleIndexAnimationDone = this.handleIndexAnimationDone.bind(this);
    this.handleSetNameInput = this.handleSetNameInput.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleImageError = this.handleImageError.bind(this);
    this.handleImageLoad = this.handleImageLoad.bind(this);
  }

  componentDidMount() {
    this.setState({ attachments: this.props.attachments }, () => {
      this.setSelectedPhoto();
    });
  }

  handleClickOutside(event) {
    if (this.nameInput && !this.nameInput.contains(event.target)) {
      this.savePhotoRename(event);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside, false);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.initialAttachmentId != this.props.initialAttachmentId) {
      this.setSelectedPhoto();
    }
  }

  setSelectedPhoto() {
    const { attachments } = this.state;
    const { initialAttachmentId } = this.props;
    if (attachments && attachments.length) {
      const selectedIndex = initialAttachmentId 
        ? attachments.findIndex(attachment => attachment.id == initialAttachmentId)
        : 0;
      const currentAttachment = attachments[selectedIndex];
      if (currentAttachment) {
        this.setState({
          selectedIndex, 
          editing: false,
          newAttachmentName: null, 
          currentAttachment, 
          carouselLeft: (this.carouselWidth * -selectedIndex),
        });
      }
    }
  }

  /** RENAME/DELETE FUNCTIONS */
  changePhotoName(e) {
    this.setState({ newAttachmentName: e.target.value, editing: true });
  }

  createPopUp(message, okButtonText, handleOk=null, handleCancel=null) {
    dataModel.showGlobalModal('SYSTEM_POPUP', {
      height: 220,
      width: 320,
      message: message,
      cancelButtonText: 'Cancel',
      okButtonText,
      handleOwnerOk: () => {
        return (typeof(handleOk) == 'function') ? handleOk() : handleOk;
      },
      handleOwnerCancel: () => {
        return (typeof(handleCancel) == 'function') ? handleCancel() : handleCancel;
      },
    });
  }

  deleteAttachment() {
    let { currentAttachment, attachments, isDeleting } = this.state;

    if (isDeleting){
      return;
    }
    this.setState({ isDeleting: true });

    const { handleClose, handleDelete, parentDelete } = this.props;
    if (parentDelete && handleDelete) {
      handleDelete(currentAttachment);
      this.setState({ isDeleting: false });
      return;
    }
    let index = attachments.findIndex(attachment => {
      return attachment.id == currentAttachment.id;
    });
    if (index >= 0) {
      if (this.props.completionStep) {
        this.analyticsLogger.logCSPhotoActivity('CompletionStepDeleted');
      }
      attachments.splice(index, 1);
      if (attachments.length > 0) {
        const newCurrentAttachment = index < attachments.length ? attachments[index] : attachments[0];
        this.setState({ currentAttachment: newCurrentAttachment, attachments });
        if (index == attachments.length) {
          this.handleArrowLeft();
        }
        dataModel.attachmentsDAO.deleteAttachment(currentAttachment.id).then(() => {
          if (handleDelete) {
            handleDelete(currentAttachment);
          }      
          this.setState({ isDeleting: false });
        });
      }
      else if (handleClose) {
        dataModel.attachmentsDAO.deleteAttachment(currentAttachment.id).then(() => {
          if (handleDelete) {
            handleDelete(null);
          }
          handleClose();
          this.setState({ isDeleting: false });
        });
      }
    }
  }
  
  handleDelete() {
    const attachmentName = this.state.currentAttachment.name ? `"${FileUtil.trimExtension(this.state.currentAttachment.name)}"` : 'this attachment';
    const message = 'Are you sure you want to delete '+attachmentName+'? This action cannot be undone.';
    this.createPopUp(message, 'Yes', this.deleteAttachment);
  }

  handleEdit() {
    const { handleClose } = this.props;
    // find a file input and click on it
    const fileInput = document.querySelector('.company-logo-container input[type="file"]');

    // Remove any previous event listener to avoid duplicates
    fileInput.onchange = null;

    fileInput.onchange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        // File selected
        handleClose();
        // set selected photo in state
        this.setSelectedPhoto(); 
      }
      // If no file selected (cancel), do nothing
      // Optionally, you can clear the value: e.target.value = '';
    };

    fileInput.click();
  }

  handleSetNameInput(element) {
    if (element) {
      this.nameInput = element;
      this.nameInput.focus();
    }
  }
  
  handleRename(e) {
    e.preventDefault();

    if (this.props.hasRename) {
      const { editingEnabled, currentAttachment } = this.state;
      if (!editingEnabled) {
        this.setState({ editingEnabled: true, newAttachmentName: FileUtil.trimExtension(currentAttachment.name) });
        document.addEventListener('click', this.handleClickOutside, false);
        // this.nameInput.focus()
      }
    }
  }

  maybeRevertPhotoName(e) {
    if (e.key == 'Escape') {
      const { editingEnabled, currentAttachment } = this.state;
      if (editingEnabled && currentAttachment) {
        this.setState({ newAttachmentName: FileUtil.trimExtension(currentAttachment.name) });
      }
    }
  }

  async savePhotoRename(e) {
    e.preventDefault();
    const { currentAttachment, newAttachmentName } = this.state;
    const attachmentNameIsValid = FileUtil.nameIsValid(newAttachmentName);

    //Check for special characters
    if (attachmentNameIsValid && FileUtil.trimExtension(currentAttachment.name) != newAttachmentName) {
      const attachmentExtension = FileUtil.getExtension(currentAttachment.name);
      currentAttachment.name = newAttachmentName + '.' + attachmentExtension;
      this.setState({ currentAttachment, editingEnabled: false });
      document.removeEventListener('click', this.handleClickOutside, false);
      await dataModel.attachmentsDAO.updateAttachment(currentAttachment, null, { recordDescriptor: { attachment: { user: { } } } });
      if (this.props.completionStep) {
        this.analyticsLogger.logCSPhotoActivity('CompletionStepRenamed');
      }
      document.activeElement.blur();
    } else if (!attachmentNameIsValid) {
      const message = 'Name cannot be empty and cannot contain any of the following characters:\n \\ / : ; {} [] <>  \' " ~ . , ! @ # $ % & *';
      this.createPopUp(message, 'OK');
      this.nameInput.focus();
    } else {
      this.setState({ editingEnabled: false });
      document.removeEventListener('click', this.handleClickOutside, false);
    }
  }

  handleArrowLeft() {
    if (this.state.selectedIndex > 0){
      this.setState({ animateLeft: true });
    }
    this.analyticsLogger.logGenericCSEvent('PhotoCarouselClickThrough');
  }

  handleArrowRight() {
    const { attachments } = this.state;
    if (this.state.selectedIndex < attachments.length - 1){
      this.setState({ animateRight: true });
    }
    this.analyticsLogger.logGenericCSEvent('PhotoCarouselClickThrough');
  }

  /** ANIMATION FUNCTIONS */
  handleLeftAnimationDone() {
    const { attachments } = this.state;
    this.animationInProgress = false;
    const selectedAttachment = attachments[this.state.selectedIndex - 1];
    this.setState({
      currentAttachment: selectedAttachment,
      newAttachmentName: null,
      editing: false,
      selectedIndex: this.state.selectedIndex - 1,
      animateLeft: false, 
      carouselLeft: this.state.carouselLeft + this.carouselWidth,
    });
  }

  handleRightAnimationDone() {
    const { attachments } = this.state;
    this.animationInProgress = false;
    const selectedAttachment = attachments[this.state.selectedIndex + 1];
    this.setState({
      currentAttachment: selectedAttachment,
      newAttachmentName: null, 
      editing: false,
      selectedIndex: this.state.selectedIndex + 1,
      animateRight: false, 
      carouselLeft: this.state.carouselLeft - this.carouselWidth,
    });
  }

  handleIndexAnimationDone() {
    const { attachments } = this.state;
    this.animationInProgress = false;
    this.setState({ 
      selectedIndex: this.state.animateToIndex, 
      currentAttachment: attachments[this.state.animateToIndex],
      editing: false,
      newAttachmentName: null,
      animateToIndex: null, 
      carouselLeft: this.state.carouselLeft - (this.carouselWidth * this.state.animateToIndex),
    });
  }

  handleInitializeElement(element){
    if (element && !this.carouselWidth) {
      this.carouselWidth = element.clientWidth;
    }
  }

  handleImageLoad(){
    this.setState({ isLoading: false });
  }

  handleImageError(){
    this.setState({ isLoading: false });
  }

  render() {
    const { completionStep, handleClose, hasDelete, hasEdit } = this.props;
    const { attachments, currentAttachment, newAttachmentName } = this.state;

    if (!attachments) {
      return null;
    }

    let instructionsClass = completionStep && completionStep.instructions ? '' : ' instructions-cont--hidden';
    const isMobile = !!window.cordova;

    // Content
    let photos = attachments.map(attachment => {
      let attachmentName = '';
      if (currentAttachment && currentAttachment.id == attachment.id) {
        attachmentName = currentAttachment.name && FileUtil.trimExtension(currentAttachment.name);
      }
      else {
        attachmentName = attachment && attachment.name ? FileUtil.trimExtension(attachment.name) : '';
      }
      let attachment_src = attachment.upload
        ? (attachment.upload instanceof Blob) ? URL.createObjectURL(attachment.upload) : attachment.upload
        : '';
      const renameSelected = currentAttachment && currentAttachment.id == attachment.id && this.state.editingEnabled ? '--active' : '';
      return (
        <article className='PhotoDisplay' key={'photo-display-' + attachment.id}>
          <form onSubmit={e => this.savePhotoRename(e)}>
            <div className='img-container'>
              <div className='attachment-info-cont'>
                  <div className={'photo-name-cont photo-name-cont' + renameSelected} ref={el => this.titelRef = el} onClick={this.handleRename}>
                    { currentAttachment && currentAttachment.id == attachment.id && this.state.editingEnabled ? 
                      (<Input 
                        className={'photo-name photo-name' + renameSelected} 
                        value={this.state.editing ? newAttachmentName : attachmentName} 
                        ref={ el => this.handleSetNameInput(el)}
                        onChange={ e => this.changePhotoName(e) }
                        onKeyDown={ e => this.maybeRevertPhotoName(e) } 
                      />) :
                      (<label className='photo-name photo-name'>{attachmentName}</label>)
                    }
                </div>
              </div>
              {this.state.isLoading && <Home type="transparent" />}
              <img src={attachment_src} alt="upload" onLoad={this.handleImageLoad} onError={this.handleImageError}/>
              <AttachmentMetadata attachment={attachment} />
            </div>
            
          </form>
        </article>
      );

    });

    // Animations
    let animate = null;

    const carouselWidth = {
      width: attachments.length * (this.carouselWidth ? this.carouselWidth : window.innerWidth),
      left: this.state.carouselLeft,
    };

    if (this.state.animateLeft && !this.animationInProgress) {
      this.animationInProgress = true;
      animate = <Animate component_id="photo-carousel-inner"
        key="photo_carousel-left"
        property={{ left: { startVal: this.state.carouselLeft, endVal: this.state.carouselLeft + this.carouselWidth } }}
        completionCallback={this.handleLeftAnimationDone} duration={500}
      />;
    }

    if (this.state.animateRight && !this.animationInProgress) {
      this.animationInProgress = true;
      animate = <Animate component_id="photo-carousel-inner"
        key="photo_carousel-right"
        property={{ left: { startVal: this.state.carouselLeft, endVal: this.state.carouselLeft - this.carouselWidth } }}
        completionCallback={this.handleRightAnimationDone} duration={500}
      />;
    }

    if (this.state.animateToIndex && !this.animationInProgress && this.carouselWidth) {
      this.animationInProgress = true;
      animate = <Animate component_id="photo-carousel-inner"
        key="photo_carousel-to_index"
        property={{ left: { startVal: this.state.carouselLeft, endVal: this.state.carouselLeft - (this.carouselWidth * this.state.animateToIndex) } }}
        completionCallback={this.handleIndexAnimationDone} duration={100}
      />;
    }

    console.log('this', this);

    // Arrows
    let leftArrow = null;
    let rightArrow = null;

    if (attachments.length > 1 && this.state.selectedIndex > 0){
      leftArrow = <div className="arrow left-arrow" onClick={this.handleArrowLeft}/>;
    }
    if (attachments.length > 1 && this.state.selectedIndex < attachments.length - 1){
      rightArrow = <div className="arrow right-arrow" onClick={this.handleArrowRight}/>;
    }

    // Pips
    let pips = null;
    if (attachments.length > 1) {
      const innerPips = attachments.map(attachment => {
        return <div className={'pip' + (currentAttachment && currentAttachment.id == attachment.id ? ' selected' : '')} key={attachment.id} />;
      });
      pips = <div className="attachment-pips" key={'pips'}>{innerPips}</div>;
    }

    const isInspection = completionStep && completionStep.completion_type == 'inspection';
    return (
      <div className={'photo-carousel-display' + (isInspection ? ' photo-carousel-display--inspection' : '')} ref={element => {this.handleInitializeElement(element);}}>
        {animate}
        <div className={ 'instructions-cont' + instructionsClass }>
          <h2>Instructions</h2>
          <p>{ completionStep && completionStep.instructions }</p>
        </div>
        {hasEdit && <div id="edit-button" className={'edit-button' + (!isMobile ? ' edit-button--web' : '')} onClick={this.handleEdit} />}
        {hasDelete && <div id="delete-button" className={'delete-button' + (!isMobile ? ' delete-button--web' : '')} onClick={this.handleDelete} />}
        {!isMobile && <span id={'close-button'} className={'close-button' + (!isMobile ? ' close-button--web' : '')} onClick={handleClose}>&times;</span>}
        {leftArrow}
        {rightArrow}
        <div className="photo-carousel-inner" id="photo-carousel-inner" style={carouselWidth}>
          {photos}
        </div>
        {pips}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.new_users ? state.new_users.currentUser : null,
  };
};
export default connect(mapStateToProps)(PhotoCarousel);



// WEBPACK FOOTER //
// ./src/components/PhotoCarousel/index.js